/**
 * Ren geometri för kundens beskärning. Inget React, inget DOM, ingen canvas —
 * allt som går att göra fel här går att testa utan att rendera något.
 *
 * Beskärningen uttrycks som en rektangel i KÄLLBILDENS pixlar. Zoom är alltså
 * "mindre rektangel", inte en skalfaktor på en transform, och invarianten går
 * att kontrollera direkt: rektangeln måste ligga innanför källbilden.
 */

import type { TryOnFraming } from '../../types/tryOn';

/** Bredd delat med höjd. Låst till gpt-image-2:s porträttformat 1024x1536. */
export const CROP_ASPECT = 2 / 3;
/** Utdatabredden kapas här. Höjden följer av formatet. */
export const OUTPUT_MAX_WIDTH = 1024;
/** Hur långt in kunden får zooma, räknat från maximalt utzoomat. */
export const MAX_ZOOM = 4;
/**
 * Golv för hur få KÄLLPIXLAR en export får bygga på. Servern skalar upp till
 * 1024x1536 ändå, och face-align bygger sin mall ur Sobel-kanter i den
 * uppskalade bilden. En export på ett par hundra pixlar blir gröt, mallen
 * tappar kontrast och preserveFace avvisar jobbet EFTER att genereringen
 * kostat ~$0.062. Zoomen får aldrig kosta mer upplösning än så här.
 */
export const MIN_EXPORT_WIDTH = 512;
/**
 * q90, inte q85. Kundens huvudpixlar hämtas ur PRECIS den här filen när
 * face-preserve klistrar tillbaka ansiktet — det är den enda platsen i
 * kedjan där komprimeringen hamnar i ansiktet. ~60 kB extra mot q85.
 */
export const EXPORT_JPEG_QUALITY = 0.9;
/**
 * Ringguidens läge, andel av ramens höjd. Detta är de ENDA otunade siffrorna
 * i beskärningen — samma status som MIN_ALIGN_SCORE i face-preserve. De
 * behöver ingen ny telemetri för att tunas: preserveFace loggar redan
 * `inputHead` för varje lyckat jobb.
 */
export const RING_CENTER_Y_RATIO = 0.105;
export const RING_DIAMETER_RATIO = 0.13;
/**
 * Var vi ANTAR att huvudets mitt sitter i ett helkroppsfoto, som andel av
 * bildens höjd. Det är en gissning — det finns ingen ansiktsdetektering här,
 * och den är bortvald på mätning: serverproben tar 2,1–3,4 s (alltså efter att
 * kunden börjat dra), och MediaPipe väger 1,5–3 MB utan native-väg i Safari
 * på iOS. Samma status som ringkonstanterna: preserveFace loggar `inputHead`
 * för varje lyckat jobb, så den går att tuna mot riktiga jobb.
 */
export const ASSUMED_HEAD_CENTRE_Y = 0.12;

export interface SourceSize {
  width: number;
  height: number;
}

/** Beskärningsrektangel i källbildens pixlar. */
export interface CropRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

function clampNumber(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

/** Största 2:3-rektangel som ryms i källbilden. */
export function maxCropWidth(source: SourceSize): number {
  return Math.min(source.width, source.height * CROP_ASPECT);
}

export function minCropWidth(source: SourceSize): number {
  const max = maxCropWidth(source);
  // Math.min mot max: en källbild som redan är mindre än golvet får helt
  // enkelt ingen zoom, i stället för ett inverterat klampintervall.
  return Math.min(max, Math.max(max / MAX_ZOOM, MIN_EXPORT_WIDTH));
}

/** Den zoom kunden faktiskt kan nå för DEN HÄR källbilden. Kan vara < MAX_ZOOM. */
export function maxZoomLevel(source: SourceSize): number {
  return maxCropWidth(source) / minCropWidth(source);
}

/**
 * Utgångsläget: maximalt utzoomat, horisontellt centrerat, och placerat i
 * höjdled så att det antagna huvudet hamnar på ringlinjen. För fotbilden
 * (`'feet'`) ankras det i nederkanten i stället, där skorna står.
 *
 * Räckvidden är smal med flit. En källbild i 2:3 eller bredare — inklusive
 * 3:4 från de flesta telefoner — täcks redan i hela sin höjd vid maximal
 * utzoomning, så clampCrop tvingar tillbaka top till 0 och beteendet är
 * exakt som `normalizeInputImage` gör i dag med `position: 'top'`. Bara
 * bilder HÖGRE än 2:3 har utrymme att placera i. Det gör ingen skada att
 * försöka: beskärningen är interaktiv, så ett felaktigt utgångsläge syns
 * direkt och går att dra rätt.
 */
export function initialCrop(source: SourceSize, framing: TryOnFraming = 'full'): CropRect {
  const width = maxCropWidth(source);
  const height = width / CROP_ASPECT;
  // Fotbilden ankras i nederkanten: beskärs något bort ska det vara knäna, inte skorna.
  const top =
    framing === 'feet'
      ? source.height - height
      : source.height * ASSUMED_HEAD_CENTRE_Y - height * RING_CENTER_Y_RATIO;
  return clampCrop({ left: (source.width - width) / 2, top, width, height }, source);
}

/**
 * Skozonen i samma viewBox som ringen ("0 0 2 3"). Ett POSITIONSmål precis som
 * ringen: den säger var skorna ska stå, inte hur stora de ska vara.
 */
export const FEET_GUIDE = { x: 0.15, y: 2.0, width: 1.7, height: 0.85, rx: 0.08 } as const;

/**
 * Håller beskärningen innanför källbilden och i rätt format.
 *
 * Math.max(0, ...) på panoreringsutrymmet är inte överflödigt: vid maximal
 * utzoomning blir höjden källhöjden multiplicerad med 2/3 och sedan med 3/2,
 * och flyttalsavrundningen kan landa någon miljarddel ÖVER källhöjden. Utan
 * skyddet blir clampNumber-intervallet inverterat.
 */
export function clampCrop(crop: CropRect, source: SourceSize): CropRect {
  const width = clampNumber(crop.width, minCropWidth(source), maxCropWidth(source));
  const height = width / CROP_ASPECT;
  return {
    left: clampNumber(crop.left, 0, Math.max(0, source.width - width)),
    top: clampNumber(crop.top, 0, Math.max(0, source.height - height)),
    width,
    height,
  };
}

/** Flyttar beskärningen. dx/dy anges i källbildens pixlar. */
export function panCrop(crop: CropRect, source: SourceSize, dx: number, dy: number): CropRect {
  return clampCrop({ ...crop, left: crop.left + dx, top: crop.top + dy }, source);
}

/**
 * Zoomar kring beskärningens mittpunkt. factor > 1 = zooma IN = mindre
 * rektangel. Bredden klampas FÖRE mittpunkten räknas om, annars glider
 * mitten när zoomen slår i taket.
 */
export function zoomCrop(crop: CropRect, source: SourceSize, factor: number): CropRect {
  const width = clampNumber(crop.width / factor, minCropWidth(source), maxCropWidth(source));
  const height = width / CROP_ASPECT;
  const centerX = crop.left + crop.width / 2;
  const centerY = crop.top + crop.height / 2;
  return clampCrop(
    { left: centerX - width / 2, top: centerY - height / 2, width, height },
    source,
  );
}

/** 1 = maximalt utzoomat, MAX_ZOOM = maximalt inzoomat. Slidern läser detta. */
export function zoomLevel(crop: CropRect, source: SourceSize): number {
  return maxCropWidth(source) / crop.width;
}

/** Sätter en absolut zoomnivå. Slidern skriver detta. */
export function setZoom(crop: CropRect, source: SourceSize, zoom: number): CropRect {
  const targetWidth = maxCropWidth(source) / clampNumber(zoom, 1, MAX_ZOOM);
  return zoomCrop(crop, source, crop.width / targetWidth);
}

/**
 * Utdatastorleken. Skalar ALDRIG upp: en 480 px bred telefonbild exporteras
 * som 480 px och inte som 1024. Uppskalning kostar byten utan att tillföra
 * detalj, och servern skalar upp till 1024x1536 ändå — men utan att beskära,
 * eftersom formatet redan stämmer.
 */
export function outputSize(crop: CropRect): { width: number; height: number } {
  const width = Math.min(OUTPUT_MAX_WIDTH, Math.round(crop.width));
  return { width, height: Math.round(width / CROP_ASPECT) };
}

/**
 * Golv för hur många EXPORTERADE pixlar ansiktet får bygga på.
 *
 * Under detta skalar servern upp så hårt att både detectHeadBox och det
 * kompositerade ansiktet vid 900 px tappar skärpa. Det är ett foto med för
 * låg upplösning, inte ett zoomval: en 480x640-bild ger ~83 px, en
 * 3024x4032 ger ~200 px.
 *
 * Siffran är en GISSNING, samma status som ringkonstanterna och
 * ASSUMED_HEAD_CENTRE_Y. I praktiken betyder den "källbildens användbara
 * bredd under ~564 px". Den går att tuna mot riktiga jobb: preserveFace
 * loggar `inputHead` och `score` för varje jobb, och avvisade jobb loggar
 * sin score just för det ändamålet.
 */
export const MIN_FACE_PX = 110;

/**
 * Ansiktets ANTAGNA höjd i exporterade pixlar. Ringen upptar
 * RING_DIAMETER_RATIO * zoom av ramens höjd, och ramen ÄR exporten.
 *
 * I det intervall där vinken faktiskt slår till är måttet OBEROENDE av zoom.
 * Exporten skalar aldrig upp, så under 1024 px kancellerar crop.width:
 * måttet blir 1,5 * RING_DIAMETER_RATIO * maxCropWidth. Vinken slår till
 * först under maxCropWidth ~564 px, alltså alltid i det intervallet. Att
 * zooma in ger färre källpixlar i en mindre export och lika många
 * ansiktspixlar — det är fysiskt riktigt, och därför nämner vinktexten
 * bara en bättre bild.
 */
export function impliedFacePx(crop: CropRect, source: SourceSize): number {
  return outputSize(crop).height * RING_DIAMETER_RATIO * zoomLevel(crop, source);
}

/**
 * Ringguiden uttryckt i en SVG-viewBox på "0 0 2 3". Då slipper overlayen
 * mäta pixlar: samma tal fungerar oavsett hur stor ramen råkar renderas.
 *
 * Ringen är ett POSITIONSMÅL. Den säger var ansiktet ska sitta i höjdled,
 * inte hur stort det ska vara — därför växer den med zoomen i stället för att
 * tvinga fram en. Med fast storlek krävde det att träffa ringen en inzoomning
 * som beskar kroppen vid låren, vilket är acceptabelt för skjortor och kavajer
 * men gör byxor och skor omöjliga att bedöma.
 */
export function ringGuideViewBox(zoom: number): { cx: number; cy: number; r: number } {
  const frameHeight = 3;
  const level = clampNumber(zoom, 1, MAX_ZOOM);
  // Ingen takregel. Ett tak nås redan vid zoom ~3,08 och får ringen att sluta
  // växa, alltså att LJUGA om den huvudstorlek inramningen innebär — och det
  // behövs inte: vid MAX_ZOOM spänner ringen y ∈ [0; 1,56] av 3 och
  // x ∈ [0,22; 1,78] av 2, alltså väl innanför ramen. Måttet måste dessutom
  // följa impliedFacePx, som räknar på samma RING_DIAMETER_RATIO * zoom.
  const r = (frameHeight * RING_DIAMETER_RATIO * level) / 2;
  // Mitten ligger på ringlinjen, men knuffas ned precis så mycket att cirkeln
  // aldrig skär över ramens överkant.
  const cy = Math.max(frameHeight * RING_CENTER_Y_RATIO, r);
  return { cx: 1, cy, r };
}
